/** Approved Icon Asset Registry — graphic resources only.
 *
 * VISTOERA_GLOBAL_FUNCTION_ICON_ASSET_REGISTRY_ASSIGNMENT_AND_ALL_SURFACE_PROPAGATION_V2
 * (deploy-trigger note: keep catalog keys in sync with backend allowlist)
 *
 * Icon Asset ≠ Function identity. Labels are graphic names (麦克风 / 音波),
 * never business function names (生成声音 / 发布). Function → icon_key
 * assignment lives in Node Registry defaults + Admin overrides. */
import {
  ApiOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  AudioOutlined,
  BarChartOutlined,
  BellOutlined,
  BgColorsOutlined,
  BlockOutlined,
  BookOutlined,
  BranchesOutlined,
  BulbOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  ClusterOutlined,
  CompassOutlined,
  ControlOutlined,
  CopyOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DeploymentUnitOutlined,
  ExperimentOutlined,
  ExportOutlined,
  EyeOutlined,
  FastForwardOutlined,
  FieldTimeOutlined,
  FileDoneOutlined,
  FileImageOutlined,
  FileTextOutlined,
  FilterOutlined,
  FireOutlined,
  FlagOutlined,
  FolderOpenOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  FundOutlined,
  GatewayOutlined,
  GlobalOutlined,
  HeartOutlined,
  HighlightOutlined,
  IdcardOutlined,
  InboxOutlined,
  LinkOutlined,
  LineChartOutlined,
  MessageOutlined,
  NodeIndexOutlined,
  NotificationOutlined,
  NumberOutlined,
  PartitionOutlined,
  PauseCircleOutlined,
  PictureOutlined,
  PieChartOutlined,
  PlayCircleOutlined,
  PlaySquareOutlined,
  PushpinOutlined,
  RadarChartOutlined,
  ReadOutlined,
  ReloadOutlined,
  RetweetOutlined,
  RobotOutlined,
  RocketOutlined,
  ScanOutlined,
  ScissorOutlined,
  SendOutlined,
  ShareAltOutlined,
  SkinOutlined,
  SlidersOutlined,
  SoundOutlined,
  StarOutlined,
  StockOutlined,
  StopOutlined,
  SyncOutlined,
  TableOutlined,
  TagOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  TrophyOutlined,
  UploadOutlined,
  UserOutlined,
  VideoCameraOutlined,
  WifiOutlined,
} from '@ant-design/icons'
import type { ReactNode } from 'react'

export type IconAssetCategory =
  | 'media'
  | 'audio'
  | 'ai'
  | 'motion'
  | 'assets'
  | 'workflow'
  | 'publish'
  | 'analytics'
  | 'utility'

export interface NodeIconCatalogEntry {
  icon: ReactNode
  /** Graphic asset name — never a Function display name. */
  label: string
  category: IconAssetCategory
  tags: readonly string[]
  sortOrder?: number
}

/**
 * Stable icon_key → approved Ant Design component.
 * Legacy keys (image-generation, publish, …) kept for DB override compat;
 * labels migrated to asset names.
 */
export const NODE_ICON_CATALOG = {
  // --- Media ---
  'image-generation': {
    icon: <FileImageOutlined />,
    label: '图片',
    category: 'media',
    tags: ['image', 'picture', 'media'],
    sortOrder: 10,
  },
  'video-generation': {
    icon: <PlaySquareOutlined />,
    label: '视频',
    category: 'media',
    tags: ['video', 'media', 'play'],
    sortOrder: 20,
  },
  'image-asset': {
    icon: <PictureOutlined />,
    label: '图片框',
    category: 'assets',
    tags: ['image', 'asset', 'frame'],
    sortOrder: 30,
  },
  'video-asset': {
    icon: <VideoCameraOutlined />,
    label: '摄像机',
    category: 'assets',
    tags: ['video', 'camera', 'asset'],
    sortOrder: 40,
  },
  camera: {
    icon: <CameraOutlined />,
    label: '相机',
    category: 'media',
    tags: ['camera', 'photo', 'media'],
    sortOrder: 50,
  },
  scan: {
    icon: <ScanOutlined />,
    label: '扫描',
    category: 'media',
    tags: ['scan', 'capture'],
    sortOrder: 60,
  },
  eye: {
    icon: <EyeOutlined />,
    label: '预览',
    category: 'media',
    tags: ['eye', 'preview', 'view'],
    sortOrder: 70,
  },

  // --- Audio (must offer multiple distinct glyphs) ---
  audio: {
    icon: <AudioOutlined />,
    label: '麦克风',
    category: 'audio',
    tags: ['audio', 'mic', 'voice', 'speech', 'sound'],
    sortOrder: 100,
  },
  mic: {
    icon: <AudioOutlined />,
    label: '麦克风',
    category: 'audio',
    tags: ['audio', 'mic', 'microphone', 'voice', 'speech'],
    sortOrder: 101,
  },
  'voice-generation': {
    icon: <SoundOutlined />,
    label: '音波',
    category: 'audio',
    tags: ['audio', 'waveform', 'voice', 'sound'],
    sortOrder: 110,
  },
  waveform: {
    icon: <SoundOutlined />,
    label: '音波',
    category: 'audio',
    tags: ['audio', 'waveform', 'wave', 'sound'],
    sortOrder: 111,
  },
  'audio-lines': {
    icon: <SlidersOutlined />,
    label: '声纹',
    category: 'audio',
    tags: ['audio', 'lines', 'equalizer', 'voice'],
    sortOrder: 120,
  },
  headphones: {
    icon: <CustomerServiceOutlined />,
    label: '耳机',
    category: 'audio',
    tags: ['audio', 'headphones', 'headset'],
    sortOrder: 130,
  },
  'music-sfx': {
    icon: <CustomerServiceOutlined />,
    label: '音乐',
    category: 'audio',
    tags: ['audio', 'music', 'sfx'],
    sortOrder: 140,
  },
  volume: {
    icon: <NotificationOutlined />,
    label: '音量',
    category: 'audio',
    tags: ['audio', 'volume', 'speaker'],
    sortOrder: 150,
  },
  radio: {
    icon: <WifiOutlined />,
    label: '广播',
    category: 'audio',
    tags: ['audio', 'radio', 'broadcast'],
    sortOrder: 160,
  },
  podcast: {
    icon: <BellOutlined />,
    label: '播客',
    category: 'audio',
    tags: ['audio', 'podcast', 'voice'],
    sortOrder: 170,
  },
  'audio-stock': {
    icon: <StockOutlined />,
    label: '波形图',
    category: 'audio',
    tags: ['audio', 'waveform', 'chart'],
    sortOrder: 180,
  },
  'audio-fund': {
    icon: <FundOutlined />,
    label: '频谱',
    category: 'audio',
    tags: ['audio', 'spectrum', 'fund'],
    sortOrder: 190,
  },

  // --- AI ---
  'ai-chat': {
    icon: <MessageOutlined />,
    label: '对话气泡',
    category: 'ai',
    tags: ['ai', 'chat', 'message'],
    sortOrder: 200,
  },
  'ai-deep-analysis': {
    icon: <BulbOutlined />,
    label: '灯泡',
    category: 'ai',
    tags: ['ai', 'idea', 'analysis'],
    sortOrder: 210,
  },
  'auto-complete': {
    icon: <RobotOutlined />,
    label: '机器人',
    category: 'ai',
    tags: ['ai', 'robot', 'auto'],
    sortOrder: 220,
  },
  'media-analysis': {
    icon: <ExperimentOutlined />,
    label: '实验',
    category: 'ai',
    tags: ['ai', 'analysis', 'experiment'],
    sortOrder: 230,
  },
  sparkles: {
    icon: <StarOutlined />,
    label: '星标',
    category: 'ai',
    tags: ['ai', 'sparkle', 'star'],
    sortOrder: 240,
  },
  rocket: {
    icon: <RocketOutlined />,
    label: '火箭',
    category: 'ai',
    tags: ['ai', 'rocket', 'launch'],
    sortOrder: 250,
  },
  // VISTOERA_CANVAS_SCRIPT_GENERATION_NODE_AND_CREATIVE_ASSET_CONTEXT_
  // LINKING_V1 — `script_generation` node's own icon (distinct from the
  // pre-existing `script` key above, which is "台词"/dialogue-lines, not
  // a full screenplay generation surface).
  screenplay: {
    icon: <FileDoneOutlined />,
    label: '剧本',
    category: 'ai',
    tags: ['script', 'screenplay', 'writing'],
    sortOrder: 255,
  },
  fire: {
    icon: <FireOutlined />,
    label: '火焰',
    category: 'ai',
    tags: ['ai', 'fire', 'hot'],
    sortOrder: 260,
  },

  // --- Motion ---
  'motion-extract': {
    icon: <GatewayOutlined />,
    label: '动作门',
    category: 'motion',
    tags: ['motion', 'extract', 'gateway'],
    sortOrder: 300,
  },
  'motion-reconstruction': {
    icon: <DeploymentUnitOutlined />,
    label: '骨架',
    category: 'motion',
    tags: ['motion', 'skeleton', 'reconstruction'],
    sortOrder: 310,
  },
  'motion-asset': {
    icon: <NodeIndexOutlined />,
    label: '动作节点',
    category: 'motion',
    tags: ['motion', 'nodes', 'asset'],
    sortOrder: 320,
  },
  branches: {
    icon: <BranchesOutlined />,
    label: '分支',
    category: 'motion',
    tags: ['motion', 'branch', 'flow'],
    sortOrder: 330,
  },
  partition: {
    icon: <PartitionOutlined />,
    label: '分区',
    category: 'motion',
    tags: ['motion', 'partition'],
    sortOrder: 340,
  },
  'virtual-tryon': {
    icon: <SkinOutlined />,
    label: '服装',
    category: 'motion',
    tags: ['tryon', 'shirt', 'fashion', 'skin'],
    sortOrder: 350,
  },
  // VistoeraTASK-00161 — 服装视频换装's own icon key. Deliberately
  // distinct from 'virtual-tryon' above (image try-on swap) — this node
  // replicates a reference VIDEO's pacing/camera/pose with the tenant's
  // real product, not an image garment swap.
  'apparel-video-replication': {
    icon: <PlaySquareOutlined />,
    label: '视频服装',
    category: 'motion',
    tags: ['apparel', 'video', 'ecommerce', 'clothing'],
    sortOrder: 355,
  },

  // --- Assets / identity ---
  text: {
    icon: <FileTextOutlined />,
    label: '文本',
    category: 'assets',
    tags: ['text', 'file', 'document'],
    sortOrder: 400,
  },
  'reference-capture': {
    icon: <LinkOutlined />,
    label: '链接',
    category: 'assets',
    tags: ['link', 'reference', 'capture'],
    sortOrder: 410,
  },
  'reference-media': {
    icon: <PictureOutlined />,
    label: '参考图',
    category: 'assets',
    tags: ['reference', 'media', 'image'],
    sortOrder: 420,
  },
  shot: {
    icon: <ClusterOutlined />,
    label: '分镜格',
    category: 'assets',
    tags: ['shot', 'storyboard', 'cluster'],
    sortOrder: 430,
  },
  script: {
    icon: <MessageOutlined />,
    label: '台词',
    category: 'assets',
    tags: ['script', 'dialogue', 'message'],
    sortOrder: 440,
  },
  'virtual-ip': {
    icon: <IdcardOutlined />,
    label: '身份卡',
    category: 'assets',
    tags: ['identity', 'ip', 'card'],
    sortOrder: 450,
  },
  character: {
    icon: <UserOutlined />,
    label: '人物',
    category: 'assets',
    tags: ['character', 'user', 'person'],
    sortOrder: 460,
  },
  folder: {
    icon: <FolderOpenOutlined />,
    label: '文件夹',
    category: 'assets',
    tags: ['folder', 'asset', 'files'],
    sortOrder: 470,
  },
  inbox: {
    icon: <InboxOutlined />,
    label: '收件箱',
    category: 'assets',
    tags: ['inbox', 'asset'],
    sortOrder: 480,
  },
  read: {
    icon: <ReadOutlined />,
    label: '阅读',
    category: 'assets',
    tags: ['read', 'book', 'text'],
    sortOrder: 490,
  },
  tag: {
    icon: <TagOutlined />,
    label: '标签',
    category: 'assets',
    tags: ['tag', 'label'],
    sortOrder: 500,
  },

  // --- Workflow ---
  scene: {
    icon: <ClusterOutlined />,
    label: '场景容器',
    category: 'workflow',
    tags: ['scene', 'container', 'workflow'],
    sortOrder: 600,
  },
  'edit-composite': {
    icon: <ScissorOutlined />,
    label: '剪刀',
    category: 'workflow',
    tags: ['edit', 'cut', 'composite'],
    sortOrder: 610,
  },
  'check-optimize': {
    icon: <CheckCircleOutlined />,
    label: '勾选',
    category: 'workflow',
    tags: ['check', 'optimize', 'ok'],
    sortOrder: 620,
  },
  'production-artifact': {
    icon: <FileDoneOutlined />,
    label: '成品文件',
    category: 'workflow',
    tags: ['artifact', 'done', 'file'],
    sortOrder: 630,
  },
  'platform-adaptation': {
    icon: <AppstoreOutlined />,
    label: '宫格',
    category: 'workflow',
    tags: ['platform', 'grid', 'adapt'],
    sortOrder: 640,
  },
  lightning: {
    icon: <ThunderboltOutlined />,
    label: '闪电',
    category: 'workflow',
    tags: ['lightning', 'action', 'generic'],
    sortOrder: 650,
  },
  'generic-function': {
    icon: <AppstoreOutlined />,
    label: '通用功能',
    category: 'utility',
    tags: ['generic', 'fallback', 'utility'],
    sortOrder: 1,
  },
  sync: {
    icon: <SyncOutlined />,
    label: '同步',
    category: 'workflow',
    tags: ['sync', 'refresh', 'workflow'],
    sortOrder: 660,
  },
  reload: {
    icon: <ReloadOutlined />,
    label: '刷新',
    category: 'workflow',
    tags: ['reload', 'refresh'],
    sortOrder: 670,
  },
  control: {
    icon: <ControlOutlined />,
    label: '控制台',
    category: 'workflow',
    tags: ['control', 'console'],
    sortOrder: 680,
  },
  tool: {
    icon: <ToolOutlined />,
    label: '工具',
    category: 'workflow',
    tags: ['tool', 'wrench'],
    sortOrder: 690,
  },
  filter: {
    icon: <FilterOutlined />,
    label: '筛选',
    category: 'workflow',
    tags: ['filter', 'funnel'],
    sortOrder: 700,
  },
  block: {
    icon: <BlockOutlined />,
    label: '模块',
    category: 'workflow',
    tags: ['block', 'module'],
    sortOrder: 710,
  },
  api: {
    icon: <ApiOutlined />,
    label: '接口',
    category: 'workflow',
    tags: ['api', 'integration'],
    sortOrder: 720,
  },
  compass: {
    icon: <CompassOutlined />,
    label: '指南针',
    category: 'workflow',
    tags: ['compass', 'navigate'],
    sortOrder: 730,
  },

  // --- Publish ---
  publish: {
    icon: <ExportOutlined />,
    label: '外链',
    category: 'publish',
    tags: ['publish', 'export', 'external', 'link'],
    sortOrder: 800,
  },
  'external-link': {
    icon: <ExportOutlined />,
    label: '外链箭头',
    category: 'publish',
    tags: ['external', 'link', 'publish', 'arrow'],
    sortOrder: 810,
  },
  send: {
    icon: <SendOutlined />,
    label: '发送',
    category: 'publish',
    tags: ['send', 'publish', 'share'],
    sortOrder: 820,
  },
  share: {
    icon: <ShareAltOutlined />,
    label: '分享',
    category: 'publish',
    tags: ['share', 'publish'],
    sortOrder: 830,
  },
  upload: {
    icon: <UploadOutlined />,
    label: '上传',
    category: 'publish',
    tags: ['upload', 'publish'],
    sortOrder: 840,
  },
  'cloud-upload': {
    icon: <CloudUploadOutlined />,
    label: '云上传',
    category: 'publish',
    tags: ['cloud', 'upload', 'publish'],
    sortOrder: 850,
  },
  global: {
    icon: <GlobalOutlined />,
    label: '全球',
    category: 'publish',
    tags: ['global', 'world', 'publish'],
    sortOrder: 860,
  },

  // --- Analytics ---
  performance: {
    icon: <BarChartOutlined />,
    label: '柱状图',
    category: 'analytics',
    tags: ['chart', 'bar', 'analytics', 'performance'],
    sortOrder: 900,
  },
  'line-chart': {
    icon: <LineChartOutlined />,
    label: '折线图',
    category: 'analytics',
    tags: ['chart', 'line', 'analytics'],
    sortOrder: 910,
  },
  'pie-chart': {
    icon: <PieChartOutlined />,
    label: '饼图',
    category: 'analytics',
    tags: ['chart', 'pie', 'analytics'],
    sortOrder: 920,
  },
  dashboard: {
    icon: <DashboardOutlined />,
    label: '仪表盘',
    category: 'analytics',
    tags: ['dashboard', 'analytics'],
    sortOrder: 930,
  },
  radar: {
    icon: <RadarChartOutlined />,
    label: '雷达图',
    category: 'analytics',
    tags: ['radar', 'chart', 'analytics'],
    sortOrder: 940,
  },
  database: {
    icon: <DatabaseOutlined />,
    label: '数据库',
    category: 'analytics',
    tags: ['database', 'data'],
    sortOrder: 950,
  },
  trophy: {
    icon: <TrophyOutlined />,
    label: '奖杯',
    category: 'analytics',
    tags: ['trophy', 'metric', 'win'],
    sortOrder: 960,
  },
  heart: {
    icon: <HeartOutlined />,
    label: '喜欢',
    category: 'analytics',
    tags: ['heart', 'like', 'engagement'],
    sortOrder: 970,
  },

  // --- Spare semantic headroom (catalog V2 — unused by default seed) ---
  clipboard: {
    icon: <CopyOutlined />,
    label: '剪贴板',
    category: 'utility',
    tags: ['clipboard', 'copy'],
    sortOrder: 1000,
  },
  layers: {
    icon: <ApartmentOutlined />,
    label: '图层',
    category: 'utility',
    tags: ['layers', 'stack'],
    sortOrder: 1010,
  },
  grid: {
    icon: <TableOutlined />,
    label: '网格',
    category: 'utility',
    tags: ['grid', 'table'],
    sortOrder: 1020,
  },
  timeline: {
    icon: <FieldTimeOutlined />,
    label: '时间线',
    category: 'utility',
    tags: ['timeline', 'time'],
    sortOrder: 1030,
  },
  bookmark: {
    icon: <BookOutlined />,
    label: '书签',
    category: 'utility',
    tags: ['bookmark', 'book'],
    sortOrder: 1040,
  },
  star: {
    icon: <StarOutlined />,
    label: '星星',
    category: 'utility',
    tags: ['star', 'favorite'],
    sortOrder: 1050,
  },
  flag: {
    icon: <FlagOutlined />,
    label: '旗帜',
    category: 'utility',
    tags: ['flag', 'mark'],
    sortOrder: 1060,
  },
  pin: {
    icon: <PushpinOutlined />,
    label: '图钉',
    category: 'utility',
    tags: ['pin', 'pushpin'],
    sortOrder: 1070,
  },
  link: {
    icon: <LinkOutlined />,
    label: '链接符',
    category: 'utility',
    tags: ['link', 'url'],
    sortOrder: 1080,
  },
  hash: {
    icon: <NumberOutlined />,
    label: '井号',
    category: 'utility',
    tags: ['hash', 'number'],
    sortOrder: 1090,
  },
  palette: {
    icon: <BgColorsOutlined />,
    label: '调色板',
    category: 'utility',
    tags: ['palette', 'color'],
    sortOrder: 1100,
  },
  brush: {
    icon: <HighlightOutlined />,
    label: '画笔',
    category: 'utility',
    tags: ['brush', 'paint'],
    sortOrder: 1110,
  },
  crop: {
    icon: <ScissorOutlined />,
    label: '裁剪',
    category: 'utility',
    tags: ['crop', 'cut'],
    sortOrder: 1120,
  },
  maximize: {
    icon: <FullscreenOutlined />,
    label: '最大化',
    category: 'utility',
    tags: ['maximize', 'fullscreen'],
    sortOrder: 1130,
  },
  minimize: {
    icon: <FullscreenExitOutlined />,
    label: '最小化',
    category: 'utility',
    tags: ['minimize', 'exit'],
    sortOrder: 1140,
  },
  play: {
    icon: <PlayCircleOutlined />,
    label: '播放',
    category: 'utility',
    tags: ['play', 'media'],
    sortOrder: 1150,
  },
  pause: {
    icon: <PauseCircleOutlined />,
    label: '暂停',
    category: 'utility',
    tags: ['pause', 'media'],
    sortOrder: 1160,
  },
  stop: {
    icon: <StopOutlined />,
    label: '停止',
    category: 'utility',
    tags: ['stop', 'media'],
    sortOrder: 1170,
  },
  'skip-forward': {
    icon: <FastForwardOutlined />,
    label: '快进',
    category: 'utility',
    tags: ['skip', 'forward', 'media'],
    sortOrder: 1180,
  },
  repeat: {
    icon: <RetweetOutlined />,
    label: '重复',
    category: 'utility',
    tags: ['repeat', 'loop'],
    sortOrder: 1190,
  },
} as const satisfies Record<string, NodeIconCatalogEntry>

export type NodeIconKey = keyof typeof NODE_ICON_CATALOG

/** Canonical fallback when icon_key is null / inactive / unknown. */
export const FALLBACK_ICON_KEY: NodeIconKey = 'generic-function'

export const ICON_CATEGORY_LABELS: Record<IconAssetCategory | 'all', string> = {
  all: '全部',
  media: '图片/视频',
  audio: '音频',
  ai: 'AI',
  motion: '动作',
  assets: '素材',
  workflow: '工作流',
  publish: '发布',
  analytics: '数据',
  utility: '通用',
}

export function listNodeIconCatalog(): Array<{
  key: NodeIconKey
  icon: ReactNode
  label: string
  category: IconAssetCategory
  tags: readonly string[]
}> {
  return (Object.keys(NODE_ICON_CATALOG) as NodeIconKey[])
    .map((key) => ({
      key,
      icon: NODE_ICON_CATALOG[key].icon,
      label: NODE_ICON_CATALOG[key].label,
      category: NODE_ICON_CATALOG[key].category,
      tags: NODE_ICON_CATALOG[key].tags,
    }))
    .sort((a, b) => {
      const ao = NODE_ICON_CATALOG[a.key].sortOrder ?? 9999
      const bo = NODE_ICON_CATALOG[b.key].sortOrder ?? 9999
      return ao - bo || a.label.localeCompare(b.label, 'zh')
    })
}

export function getIconAsset(key: string | null | undefined): (typeof NODE_ICON_CATALOG)[NodeIconKey] | null {
  if (!key || !(key in NODE_ICON_CATALOG)) return null
  return NODE_ICON_CATALOG[key as NodeIconKey]
}
